import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Where this module's filters live: the address bar.
 *
 * Two problems, one answer. A filter held in component state died the moment
 * an admin opened a draft and came back — the whole tab strip unmounts for the
 * detail view — so every search had to be retyped. And a link that means "the
 * registry rows nothing maps" could not be written at all, which is why the
 * Problems screen's remedies dropped people into an unfiltered 88-row table
 * and left them to find the six rows themselves.
 *
 * Keys are namespaced by screen (`drafts.q`, `jobs.status`) so the tab
 * parameter and three screens' filters can share one query string without
 * colliding. Writes REPLACE rather than push: typing in a search box is not a
 * navigation, and an admin who filters five times should not have to press
 * Back five times to leave.
 */

export type FilterValues<Keys extends string> = Record<Keys, string>;

const qualify = (prefix: string, key: string) => `${prefix}.${key}`;

/**
 * Reads and writes one screen's filters. `defaults` names every key the
 * screen owns; a key absent from the URL reads as its default, and writing a
 * value equal to its default removes it, so an unfiltered screen has a clean
 * address.
 */
export const useModuleFilters = <Keys extends string>(
  prefix: string,
  defaults: FilterValues<Keys>,
): [FilterValues<Keys>, (patch: Partial<FilterValues<Keys>>) => void] => {
  const history = useHistory();
  const location = useLocation();
  const { pathname, search } = location;

  const values = useMemo(() => {
    const params = new URLSearchParams(search);
    const result = { ...defaults };
    (Object.keys(defaults) as Keys[]).forEach(key => {
      const raw = params.get(qualify(prefix, key));
      if (raw != null) result[key] = raw;
    });
    return result;
    // `defaults` is a literal from the caller's render; its members are the
    // dependency, and they are constants.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, prefix]);

  const setValues = useCallback(
    (patch: Partial<FilterValues<Keys>>) => {
      // Read through the router, never `window.location`: under a memory
      // history — tests, and any embedding that does not own the address bar
      // — the browser's URL never moves, and a filter written from it would
      // silently drop every other parameter on the screen.
      const params = new URLSearchParams(search);
      (Object.keys(patch) as Keys[]).forEach(key => {
        const value = patch[key];
        if (value == null || value === '' || value === defaults[key]) {
          params.delete(qualify(prefix, key));
        } else {
          params.set(qualify(prefix, key), String(value));
        }
      });
      const next = params.toString();
      history.replace(`${pathname}${next ? `?${next}` : ''}`);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, prefix, search, pathname],
  );

  return [values, setValues];
};

/** An antd Select's value: '' in the URL means "no filter", not an empty
 *  selection the control would render as a blank pill. */
export const asOption = (value: string): string | undefined =>
  value || undefined;

/** A filter that came in as a number, for the screens that scope by id. */
export const asNumber = (value: string): number | undefined => {
  const parsed = Number(value);
  return value !== '' && Number.isFinite(parsed) ? parsed : undefined;
};
